<script setup lang="ts">
// Imports
import type { SolTemplateHeader } from '@solfacil/girassol'
import { SolButton, SolTag } from '@solfacil/girassol'
import IconChevronRight from '~icons/material-symbols/chevron-right'
import Loading from '~/components/loading/index.vue'
import { useFinancingRoute } from '~/composables/financing/useFinancingRoute'
import useFinancingSearch, { type FinancingSearchItem } from '~/store-v2/financings/useFinancingSearch'

// Types
type TagType = 'positive' | 'warning' | 'negative' | 'informative' | 'neutral'

type HeaderWithSearchMenu = InstanceType<typeof SolTemplateHeader> & {
  closeSearchMenu?: () => void
  clearSearch?: () => void
  clear?: () => void
}

// Props
const props = withDefaults(defineProps<{
  searchValue: string
  searchedValue?: string
  loading?: boolean
  partnerId?: string | number
  header?: HeaderWithSearchMenu | null
}>(), {
  loading: false,
})

// Composables/Hooks
const { t } = useI18n()
const router = useRouter()
const { buildRouteUrl } = useFinancingRoute()

// Computed
const typedText = computed(() => props.searchValue.trim())

const submittedQuery = computed(() => (props.searchedValue ?? '').trim())

const normalizedPartnerId = computed(() => {
  const value = props.partnerId
  if (value === '' || value === null || value === undefined)
    return undefined
  return value.toString()
})

const shouldFetch = computed(() => submittedQuery.value.length > 0)

const searchParams = computed(() => ({
  query: submittedQuery.value,
  page: 1,
  partner_id: normalizedPartnerId.value ?? '',
  steps: [],
  status: [],
  is_combo: '',
  page_size: 4,
  order_by: 'updated_at:desc',
}))

const { data, isFetching } = useFinancingSearch(searchParams, { enabled: shouldFetch })

const results = computed(() => {
  return (data.value?.data ?? []) as FinancingSearchItem[]
})

const isLoading = computed(() => props.loading || isFetching.value)

// Constants
const STATUS_TAG_TYPE: Record<string, TagType> = {
  approved: 'positive',
  in_progress: 'warning',
  under_analysis: 'informative',
  reproved: 'negative',
  expired: 'negative',
  canceled: 'negative',
  to_review: 'warning',
  waiting_customer: 'warning',
}

const STATUS_LABEL: Record<string, string> = {
  approved: t('header.search.status.approved'),
  in_progress: t('header.search.status.in_progress'),
  under_analysis: t('header.search.status.under_analysis'),
  reproved: t('header.search.status.reproved'),
  expired: t('header.search.status.expired'),
  canceled: t('header.search.status.canceled'),
  to_review: t('header.search.status.to_review'),
  waiting_customer: t('header.search.status.waiting_customer'),
}

const STEP_LABEL: Record<string, string> = {
  simulation: t('header.search.step.simulation'),
  equipment: t('header.search.step.equipment'),
  customer_data: t('header.search.step.customer_data'),
  biometry_and_contract: t('header.search.step.biometry_and_contract'),
  payment_model: t('header.search.step.payment_model'),
  installation: t('header.search.step.installation'),
}

// Functions
function getTagType(status: string): TagType {
  return STATUS_TAG_TYPE[status] || 'neutral'
}

function getStatusLabel(status: string): string {
  return STATUS_LABEL[status] || status
}

function getStepLabel(step: string): string {
  return STEP_LABEL[step] || step
}

function buildProjectUrl(item: FinancingSearchItem): string {
  return buildRouteUrl(item)
}

function closeHeaderSearch() {
  props.header?.closeSearchMenu?.()
  props.header?.clearSearch?.()
  props.header?.clear?.()

  ;(document.activeElement as HTMLElement | null)?.blur?.()
}

function handleProjectClick(item: FinancingSearchItem) {
  const url = buildProjectUrl(item)
  closeHeaderSearch()
  item.new_journey === false
    ? window.open(url, '_blank')
    : router.push(url)
}

function handleMoreProjects() {
  closeHeaderSearch()
  const query = submittedQuery.value
  router.push({
    path: '/',
    query: query ? { query } : {},
  })
}
</script>

<template>
  <div class="search-projects-content">
    <div v-if="isLoading" class="search-projects-loading">
      <Loading class="loading-green" />
      <span class="loading-text">{{ t('header.search.loading.text') }}</span>
    </div>

    <div v-else-if="!typedText" class="search-projects-empty">
      <p class="empty-text">
        {{ t('header.search.empty.type_to_search') }}
      </p>
    </div>

    <div v-else-if="!submittedQuery" class="search-projects-empty">
      <p class="empty-text">
        {{ t('header.search.empty.press_enter') }}
      </p>
    </div>

    <div v-else-if="results.length === 0" class="search-projects-empty">
      <p class="empty-text">
        {{ t('header.search.empty.no_results') }}
      </p>
    </div>

    <div v-else class="search-projects-list">
      <div
        v-for="item in results"
        :key="item.id"
        class="search-project-item"
        @click="handleProjectClick(item)"
      >
        <div class="project-content">
          <div class="project-frame frame-name">
            <p class="section-label">
              {{ t('header.search.labels.project_name') }}
            </p>
            <p class="section-value project-name">
              {{ item.customer_name || `${t('header.search.project_fallback')} ${item.project_id ?? item.id}` }}
            </p>
          </div>
          <div class="project-frame frame-id-step">
            <div class="id-section">
              <p class="section-label">
                {{ t('header.search.labels.id') }}
              </p>
              <p class="section-value project-id">
                {{ item.id }}
              </p>
            </div>
            <div class="step-section">
              <p class="section-label">
                {{ t('header.search.labels.step') }}
              </p>
              <p class="section-value project-step">
                {{ getStepLabel(item.step ?? '') }}
              </p>
            </div>
          </div>
          <div class="project-frame frame-tag">
            <SolTag
              :id="`status-${item.id}`"
              class="tag-status"
              :text="getStatusLabel(item.status ?? '')"
              :type="getTagType(item.status ?? '')"
              size="medium"
            />
          </div>
        </div>
      </div>

      <SolButton
        id="more-projects"
        variant="tertiary"
        size="medium"
        class="more-projects-button"
        @click="handleMoreProjects"
      >
        <template #icon-right>
          <IconChevronRight class="icon-chevron" />
        </template>
        <span class="button-text">{{ t('header.search.labels.more_projects') }}</span>
      </SolButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.search-projects-content {
  .search-projects-loading {
    @apply flex items-center justify-center gap-2 p-4xs;

    .loading-green {
      width: 50px!important;
      height: 50px!important;
    }

    .loading-text {
      @apply fonts-body-medium-regular text-neutral-low-dark;
    }
  }

  .search-projects-empty {
    @apply flex items-center justify-center p-4xs;

    .empty-text {
      @apply fonts-body-medium-regular text-neutral-low-light;
    }
  }

  .search-projects-list {
    @apply flex flex-col gap-2;

    .search-project-item {
      @apply px-[26px] py-3xs border border-neutral-high-medium rounded-md;
      @apply cursor-pointer transition-colors;

      &:hover {
        @apply bg-neutral-high-light;
      }

      .project-content {
        @apply flex flex-wrap items-center gap-4;

        .project-frame {
          @apply flex flex-col items-start justify-center;

          &.frame-name {
            @apply flex-1 min-w-20 overflow-hidden;
          }

          &.frame-id-step {
            @apply flex flex-row items-center justify-start flex-shrink-0 gap-[50px] w-[300px];
          }

          &.frame-tag {
            @apply flex-shrink-0 w-[120px];

            .tag-status {
              @apply w-full;
            }
          }

          .section-label {
            @apply fonts-body-small-regular text-neutral-low-medium;
            @apply whitespace-nowrap;
          }

          .section-value {
            @apply fonts-body-medium-regular text-neutral-low-dark;
            @apply whitespace-nowrap;

            &.project-name {
              @apply fonts-body-medium-bold;
              @apply truncate;
              max-width: 100%;
            }
          }
        }
      }
    }
  }
}
</style>
